import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, BookOpen, Clock, Calendar } from "lucide-react";
import { EDUCATION } from "@/data/education";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";

/**
 * Renders inline citation markers [1][2] as clickable superscripts that
 * scroll to the matching entry in the SOURCES section.
 */
function withCitations(text) {
  const parts = text.split(/(\[\d+\](?:\[\d+\])*)/g);
  return parts.map((chunk, idx) => {
    const nums = chunk.match(/\d+/g);
    if (!nums) return chunk;
    return (
      <sup key={idx} className="ml-0.5">
        {nums.map((n, i) => (
          <a
            key={i}
            href={`#ref-${n}`}
            onClick={(e) => {
              e.preventDefault();
              document.getElementById(`ref-${n}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
              document.getElementById(`ref-${n}`)?.classList.add("ring-2", "ring-gold");
              setTimeout(() => document.getElementById(`ref-${n}`)?.classList.remove("ring-2", "ring-gold"), 1200);
            }}
            className="text-gold hover:text-gold-light font-mono text-[10px] px-1"
            data-testid={`cite-${n}`}
          >
            [{n}]
          </a>
        ))}
      </sup>
    );
  });
}

export default function EducationDetail() {
  const { slug } = useParams();
  const a = EDUCATION[slug];
  if (!a) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-white/60">
        Article not found. <Link to="/education" className="text-gold underline">Back to Education</Link>.
      </div>
    );
  }
  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12" data-testid={`edu-page-${a.slug}`}>
      <SEO
        title={a.title}
        description={a.summary}
        path={`/education/${a.slug}`}
        image="institute"
        type="article"
      />

      <Link to="/education" data-testid="back-to-education" className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-sm">
        <ArrowLeft className="h-4 w-4" /> Back to Education
      </Link>

      <div className="mt-6 flex flex-wrap gap-4 items-center text-[11px] font-mono tracking-widest text-white/60">
        <span className="text-gold">{a.type.toUpperCase()}</span>
        <span>·</span>
        <span>{a.category.toUpperCase()}</span>
        <span>·</span>
        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {a.read}</span>
        <span>·</span>
        <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Last reviewed {a.updated}</span>
      </div>

      <h1 className="font-display text-4xl md:text-5xl text-white mt-4 leading-tight text-balance">{a.title}</h1>
      <p className="mt-4 text-white/70 text-lg leading-relaxed">{a.summary}</p>

      <div className="divider-gold my-10" />

      <article className="space-y-8">
        {a.sections.map((s, i) => (
          <section key={i}>
            <h2 className="font-display text-2xl text-gold-gradient">{s.h}</h2>
            <p className="mt-3 text-white/80 leading-relaxed">{withCitations(s.p)}</p>
          </section>
        ))}
      </article>

      <div className="divider-gold my-10" />

      <section id="sources" data-testid="sources-section">
        <div className="text-gold font-mono text-xs tracking-widest">SOURCES & REFERENCES</div>
        <div className="mt-4 space-y-3">
          {a.sources.map((s, i) => (
            <div
              key={i}
              id={`ref-${i + 1}`}
              className="rounded-xl border border-white/10 bg-obsidian-soft p-4 transition-shadow"
              data-testid={`ref-${i + 1}`}
            >
              <div className="text-xs font-mono text-gold">[{i + 1}]</div>
              <div className="mt-1 text-white">{s.n}</div>
              <a
                href={s.u}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-1 text-gold text-xs font-mono tracking-widest hover:text-gold-light"
                data-testid={`ref-link-${i + 1}`}
              >
                VIEW SOURCE <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-10">
        <Link to="/education">
          <Button variant="outline" className="rounded-full border-gold/40 text-gold hover:bg-gold/10 bg-transparent">
            <BookOpen className="mr-2 h-4 w-4" /> More educational content
          </Button>
        </Link>
      </div>
    </div>
  );
}
